<template>
  <div>
    <form-section :title="$t('Integrations_Settings_BasicSettings')">
      <rest-form-item
        :hint="$t('Integrations_Field_Name_Description')"
        :label="$t('Integrations_Field_Name')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Integrations_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_WebhookSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_WebhookUrl')"
        prop="params.url"
      >
        <el-input
          v-model="model.params.url"
          :placeholder="$t('Integrations_Field_WebhookUrl')"
        />
      </rest-form-item>

      <rest-form-item
        :label="$t('Integrations_Field_AuthenticationType')"
        prop="params.authentication.type"
      >
        <enum-select
          v-model="model.params.authentication.type"
          :change="onChangeType()"
          :options="WebhookAuthenticationTypesLabels"
        />
      </rest-form-item>

      <rest-form-item
        v-if="model.params.authentication.type === 'bearer'"
        :label="$t('Integrations_Field_Token')"
        prop="params.authentication.token"
      >
        <el-input
          v-model="model.params.authentication.token"
          :placeholder="$t('Authentication_Type_BearerToken')"
        />
      </rest-form-item>

      <el-row v-if="model.params.authentication.type === 'basic'">
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Field_Username')"
            prop="params.authentication.username"
          >
            <el-input
              v-model="model.params.authentication.username"
              :placeholder="$t('Integrations_Field_Username')"
            />
          </rest-form-item>
        </el-col>
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Field_Password')"
            prop="params.authentication.password"
          >
            <el-input
              v-model="model.params.authentication.password"
              :placeholder="$t('Integrations_Field_Password')"
              type="password"
            />
          </rest-form-item>
        </el-col>
      </el-row>
      <el-row v-if="model.params.authentication.type === 'oauth2'">
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Field_AuthenticationUrl')"
            prop="params.authentication.authentication_url"
          >
            <el-input
              v-model="model.params.authentication.authentication_url"
              :placeholder="$t('Integrations_Field_AuthenticationUrl')"
            />
          </rest-form-item>
        </el-col>
        <el-col :span="4">
          <rest-form-item
            :label="$t('Integrations_Field_GrantType')"
            prop="params.authentication.grant_type"
          >
            <enum-select
              v-model="model.params.authentication.grant_type"
              :change="onChangeGrantType()"
              :options="OauthGrantTypesLabels"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="8">
          <rest-form-item
            :label="$t('Integrations_Field_Scope')"
            prop="params.authentication.scope"
          >
            <el-input
              v-model="model.params.authentication.scope"
              :placeholder="$t('Integrations_Field_Scope')"
            />
          </rest-form-item>
        </el-col>
        <el-col
          :span="12"
          v-if="model.params.authentication.grant_type === 'client_credentials'"
        >
          <rest-form-item
            :label="$t('Integrations_Field_ClientID')"
            prop="params.authentication.client_id"
          >
            <el-input
              v-model="model.params.authentication.client_id"
              :placeholder="$t('Integrations_Field_ClientID')"
            />
          </rest-form-item>
        </el-col>
        <el-col
          :span="12"
          v-if="model.params.authentication.grant_type === 'client_credentials'"
        >
          <rest-form-item
            :label="$t('Integrations_Field_ClientSecret')"
            prop="params.authentication.client_secret"
          >
            <el-input
              v-model="model.params.authentication.client_secret"
              :placeholder="$t('Integrations_Field_ClientSecret')"
            />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row v-if="model.params.authentication.type === 'signed'">
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Field_Signature')"
            prop="params.authentication.signature"
          >
            <el-input
              v-model="secret"
              :class="{ blurred: !showToken }"
              :disabled="true"
              type="text"
            />
            <icon
              v-if="!showToken"
              :alt="$t('ApiIntegration_Button_AppTokenReveal')"
              :title="$t('ApiIntegration_Button_AppTokenReveal')"
              glyph="eye"
              size="20"
              @click="showToken = true"
            />
            <icon
              v-else
              :alt="$t('ApiIntegration_Button_AppTokenHide')"
              :title="$t('ApiIntegration_Button_AppTokenHide')"
              glyph="eye-crossed"
              size="20"
              @click="showToken = false"
            />
          </rest-form-item>
        </el-col>
        <el-col :span="12">
          <rest-form-item :label="' '" prop="params.authentication.signature">
            <el-button @click="generateSignature()"
              >{{ $t('Integrations_Field_Signature_Button') }}
            </el-button>
          </rest-form-item>
        </el-col>
      </el-row>
      <el-row v-if="tokenError !== false">
        <el-col :span="12">
          <el-alert
            :title="$t('Integrations_Field_Signature_Error')"
            show-icon
            type="error"
          >
          </el-alert>
        </el-col>
      </el-row>
    </form-section>

    <form-section :title="$t('Integrations_Settings_WebhookMessageEvents')">
      <el-row>
        <el-col :span="12">
          <p class="list-title">
            {{ $t('Integrations_Webhook_Title_MessageEvents') }}
          </p>
          <events-toggle-list :events-list="messageEventsList" :model="model" />
        </el-col>

        <el-col :span="12">
          <p class="list-title">
            {{ $t('Integrations_Webhook_Title_MessageTypes') }}
          </p>
          <message-types-list :types-list="messageTypesList" :model="model" />
        </el-col>
      </el-row>
    </form-section>

    <form-section
      :title="$t('Integrations_Settings_WebhookSubscriptionEvents')"
    >
      <el-row>
        <el-col :span="12">
          <events-toggle-list
            :events-list="
              subscriptionEventsList.filter((element, index) => index % 2 === 0)
            "
            :model="model"
          />
        </el-col>
        <el-col :span="12">
          <events-toggle-list
            :events-list="
              subscriptionEventsList.filter((element, index) => index % 2 === 1)
            "
            :model="model"
          />
        </el-col>
      </el-row>
      <form-section
        :title="
          $t(
            'Integrations_Settings_WebhookSubscriptionPreferenceExcludeDataBySource'
          )
        "
        v-if="
          model.params.message_events['message_subscription_change'] ||
            model.params.message_events['preference_subscription_change']
        "
      >
        <el-col :span="12">
          <form-item
            v-for="sourceScenario in sourceScenarioSubscriptionList"
            :prop="sourceScenario.code"
            :label="sourceScenario.name"
            :key="sourceScenario.code"
            :description="sourceScenario.description"
          >
            <feature-toggle
              v-model="model.params.source_scenario[sourceScenario.code]"
            />
          </form-item>
        </el-col>
      </form-section>
    </form-section>

    <form-section :title="$t('Integrations_Webhook_Title_OtherSettings')">
      <el-col :span="12">
        <form-item
          prop="prevent_send_without_ext_msg_id"
          :label="
            $t('Integrations_Webhook_OtherSettings_StopSendWithoutMsgExtId')
          "
          :description="
            $t(
              'Integrations_Webhook_OtherSettings_StopSendWithoutMsgExtId_Desc'
            )
          "
        >
          <feature-toggle
            v-model="
              model.params.other_settings.prevent_send_without_ext_msg_id
            "
          />
        </form-item>
      </el-col>
      <integration-proxy-settings
        v-if="isProxyEnabled"
        v-model="model.params.other_settings.use_static_ip"
      />
      <el-col :span="12">
        <rest-form-item
          prop="params.other_settings.notify_on_error"
          :label="$t('Integrations_Webhook_OtherSettings_NotifyOnError_Title')"
          :description="
            $t('Integrations_Webhook_OtherSettings_NotifyOnError_Desc')
          "
        >
          <feature-toggle
            v-model="model.params.other_settings.notify_on_error"
          />
        </rest-form-item>
        <rest-form-item
          v-if="model.params.other_settings.notify_on_error"
          prop="params.other_settings.error_email"
        >
          <el-input
            :placeholder="$t('Integrations_Webhook_OtherSettings_Email')"
            v-model="model.params.other_settings.error_email"
          ></el-input>
        </rest-form-item>
      </el-col>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import EnumSelect from '@/common/components/form/EnumSelect'
import {
  WebhookAuthenticationTypesLabels,
  OauthGrantTypesLabels
} from '@/modules/integrations/enum/AuthenticationTypesEnum'
import { isValidUrl } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import FormItem from '@/common/components/form/FormItem.vue'
import FeatureToggle from '@/common/components/form/FeatureToggle.vue'
import store from '@/core/store'
import EventsToggleList from '@/modules/integrations/types/webhook/EventsToggleList'
import MessageTypesList from '@/modules/integrations/types/webhook//MessageTypesList.vue'
import IntegrationProxySettings from '@/modules/integrations/components/IntegrationProxySettings.vue'
import { SourceScenarioEnum } from '@/common/enum/SourceScenarioEnum'

export default {
  name: 'WebhookSettings',

  components: {
    EventsToggleList,
    FeatureToggle,
    FormItem,
    FormSection,
    RestFormItem,
    EnumSelect,
    MessageTypesList,
    IntegrationProxySettings
  },

  props: {
    model: {
      type: Object,
      required: true
    }
  },

  created() {
    if (this.isCreatePage) {
      this.$set(this.model.params, 'authentication', {
        type: 'none',
        token: null,
        signature: {
          active: false
        },
        grant_type: 'client_credentials'
      })

      this.$set(this.model.params, 'message_types', {
        ios_push: 0,
        android_push: 0,
        web_push: 0,
        inapp: 0,
        onsite: 0,
        inbox: 0,
        email: 0,
        sms: 0,
        whatsapp: 0,
        facebook: 0
      })

      this.$set(this.model.params, 'message_events', {
        message_send: 0,
        message_failure: 0,
        message_delivery: 0,
        message_bounce: 0,
        message_open: 0,
        message_click: 0,
        message_unsubscribe: 0,
        message_complaint: 0,
        message_opt: 0
      })

      this.setDefaultOtherSettings()
    } else {
      if (this.model.params.other_settings === undefined) {
        this.setDefaultOtherSettings()
      }

      // This should be a temporary check to allow backwards compatibility for existing webhook integrations
      // that had the message_subscription_change flag enabled or preference_subscription_change flag enabled
      if (
        (this.model.params.message_events.message_subscription_change ||
          this.model.params.message_events.preference_subscription_change) &&
        this.model.params.source_scenario === undefined
      ) {
        this.setDefaultSourceScenarioSettings()
      }
    }
  },

  mounted() {
    RestApi.post(`projects/${this.model.project_id}/webhook/signature/get`)
      .then(response => {
        let data = JSON.parse(response.data)
        this.model.params.authentication.signature.active = true
        this.secret = data.secret
        this.tokenError = false
      })
      .catch(error => {
        this.tokenError = error
      })
  },

  data() {
    let validateUrl = (rule, value, callback) => {
      if (isValidUrl(value)) {
        callback()
      } else {
        callback(new Error(this.urlValidationMessage))
      }
    }
    return {
      WebhookAuthenticationTypesLabels,
      OauthGrantTypesLabels,
      SourceScenarioEnum,
      showToken: false,
      tokenError: false,
      urlValidationMessage: this.$t('RestForm_FieldInvalidUrl'),
      rules: {
        name: {
          required: true,
          message: this.$t('RestForm_FieldIsRequired')
        },
        params: {
          url: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            },
            {
              validator: validateUrl
            }
          ],
          authentication: {
            type: {
              required: true
            },
            token: {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            },
            username: {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            },
            password: {
              required: false,
              message: this.$t('RestForm_FieldIsRequired')
            },
            authentication_url: [
              {
                required: true,
                message: this.$t('RestForm_FieldIsRequired')
              },
              {
                validator: validateUrl
              }
            ],
            client_id: {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            },
            client_secret: {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            },
            grant_type: {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            }
          },
          other_settings: {
            error_email: {
              required: false,
              message: this.$t('RestForm_FieldIsRequired')
            }
          }
        }
      },
      secret: null,
      messageEventsList: [
        {
          code: 'message_send',
          name: 'Message Send'
        },
        {
          code: 'message_failure',
          name: 'Message Failure'
        },
        {
          code: 'message_bounce',
          name: 'Message Bounce'
        },
        {
          code: 'message_open',
          name: 'Message Open'
        },
        {
          code: 'message_click',
          name: 'Message Click'
        },
        {
          code: 'message_delivery',
          name: 'Message Delivery'
        },
        {
          code: 'message_unsubscribe',
          name: 'Message Unsubscribe'
        },
        {
          code: 'message_complaint',
          name: 'Message Complaint'
        }
      ],
      subscriptionEventsList: [
        {
          code: 'message_subscription_change',
          name: 'Message Opt In/Out'
        },
        {
          code: 'preference_subscription_change',
          name: 'Preference Opt In/Out'
        }
      ],
      sourceScenarioSubscriptionList: [
        {
          code: SourceScenarioEnum.SCENARIO_INTEGRATION,
          name: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceIntegrationLabel'
          ),
          description: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceIntegrationDescription'
          )
        },
        {
          code: SourceScenarioEnum.SCENARIO_IMPORT,
          name: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceImportLabel'
          ),
          description: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceImportsDescription'
          )
        },
        {
          code: SourceScenarioEnum.SCENARIO_SDK_API,
          name: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceSDKAPILabel'
          ),
          description: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceSDKAPIDescription'
          )
        },
        {
          code: SourceScenarioEnum.SCENARIO_EXTERNAL_API,
          name: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceExternalAPILabel'
          ),
          description: this.$t(
            'Integrations_Settings_WebhookExcludeBySourceExternalAPIDescription'
          )
        }
      ],
      messageTypesList: [
        {
          code: 'ios_push',
          name: this.$t('Integrations_Webhook_MessageType_iOSPush')
        },
        {
          code: 'android_push',
          name: this.$t('Integrations_Webhook_MessageType_AndroidPush')
        },
        {
          code: 'web_push',
          name: this.$t('Integrations_Webhook_MessageType_WebPush')
        },
        {
          code: 'inapp',
          name: this.$t('Integrations_Webhook_MessageType_InApp')
        },
        {
          code: 'onsite',
          name: this.$t('Integrations_Webhook_MessageType_OnSite')
        },
        {
          code: 'inbox',
          name: this.$t('Integrations_Webhook_MessageType_Inbox')
        },
        {
          code: 'email',
          name: this.$t('Integrations_Webhook_MessageType_Email')
        },
        {
          code: 'sms',
          name: this.$t('Integrations_Webhook_MessageType_Sms')
        },
        {
          code: 'whatsapp',
          name: this.$t('Integrations_Webhook_MessageType_Whatsapp')
        },
        {
          code: 'facebook',
          name: this.$t('Integrations_Webhook_MessageType_Facebook')
        }
      ]
    }
  },

  computed: {
    isCreatePage() {
      return !this.$route.query.id
    },
    isProxyEnabled() {
      return store.state.app.proxy ? store.state.app.proxy['enabled'] : null
    },
    shouldNotifyOnError() {
      return this.model.params.other_settings.notify_on_error === 1
    }
  },

  methods: {
    setDefaultOtherSettings() {
      this.$set(this.model.params, 'other_settings', {
        prevent_send_without_ext_msg_id: 0,
        notify_on_error: 0
      })
    },
    setDefaultSourceScenarioSettings() {
      this.$set(this.model.params, 'source_scenario', {
        [SourceScenarioEnum.SCENARIO_INTEGRATION]: 0,
        [SourceScenarioEnum.SCENARIO_IMPORT]: 0,
        [SourceScenarioEnum.SCENARIO_SDK_API]: 0,
        [SourceScenarioEnum.SCENARIO_EXTERNAL_API]: 0
      })
    },
    generateSignature() {
      RestApi.post(
        `projects/${this.model.project_id}/webhook/signature/generate`
      )
        .then(response => {
          let data = JSON.parse(response.data)
          this.model.params.authentication.signature.active = true
          this.secret = data.secret
          this.showToken = true
          this.tokenError = false
        })
        .catch(error => {
          this.tokenError = error
        })
    },
    onChangeType() {
      if (this.model.params.authentication.type !== 'signed') {
        this.model.params.authentication.signature = {
          active: false
        }
      } else {
        this.model.params.authentication.signature = {
          active: this.secret !== null
        }
      }
    },
    onChangeGrantType() {}
  },

  watch: {
    model: {
      handler(val) {
        this.rules.params.other_settings.error_email.required = this.shouldNotifyOnError
      },
      deep: true
    },
    'model.params.message_events.message_subscription_change': {
      handler(newState) {
        if (newState && !this.model.params.source_scenario) {
          this.$set(this.model.params, 'source_scenario', {
            [SourceScenarioEnum.SCENARIO_INTEGRATION]: 1,
            [SourceScenarioEnum.SCENARIO_IMPORT]: 1,
            [SourceScenarioEnum.SCENARIO_SDK_API]: 1,
            [SourceScenarioEnum.SCENARIO_EXTERNAL_API]: 1
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.el-form-item__content .icon {
  position: absolute;
  padding: 10px;
  top: 0;
  right: 0;
}

.el-form-item__content .blurred {
  -webkit-filter: blur(2px);
  -moz-filter: blur(2px);
  -ms-filter: blur(2px);
  -o-filter: blur(2px);
  filter: blur(2px);
}

.list-title {
  font-size: 15px;
  margin-bottom: 20px;
}

.flexbox {
  display: flex;
  align-items: flex-end;
}

>>> .el-table tr:last-of-type td {
  border-bottom: none;
}
</style>



// WEBPACK FOOTER //
// src/modules/integrations/types/webhook/WebhookSettings.vue